import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { fromEvent, merge, Subscription } from 'rxjs';
import { filter, map, repeat, take } from 'rxjs/operators';

import { selectPolicyAnalytics } from '@amfam/policy/data-access/lib/+state/summaries/summaries.selectors';
import { AnalyticsFacade } from '@amfam/shared/analytics/lib/+state/analytics.facade';
import { selectTrackId } from '@amfam/shared/user/lib/+state/user.selectors';
import { WindowRef } from '@amfam/shared/utility/shared-services/lib/window/window.service';

import { isCustomerMessage } from './salesforce-chat-idle.service';
import { SalesforceChatSessionService } from './salesforce-chat-session.service';

export const CHAT_BOT_ANALYTICS_EVENTS = {
  initiation: 'chat_bot_initiation',
  chatStart: 'chat_bot_chat_start',
  chatClose: 'chat_bot_chat_close'
} as const;

type ChatBotAnalyticsEvent =
  (typeof CHAT_BOT_ANALYTICS_EVENTS)[keyof typeof CHAT_BOT_ANALYTICS_EVENTS];

/**
 * Reports customer interactions with the Salesforce chat widget to Google Analytics.
 * `afe_session_id`, `customer_group_id` and `customer_segment_cd` are appended to every
 * event by the `sendAnalytic` effect, so only the remaining params are supplied here.
 */
@Injectable({ providedIn: 'root' })
export class SalesforceChatAnalyticsService implements OnDestroy {
  private win = inject(WindowRef);
  private ngZone = inject(NgZone);
  private store = inject(Store);
  private analyticsFacade = inject(AnalyticsFacade);
  private chatSession = inject(SalesforceChatSessionService);

  private subscription: Subscription | undefined;

  /** Safe to call more than once; only the first call binds listeners. */
  start(): void {
    if (this.subscription) {
      return;
    }

    const win = this.win.nativeWindow;

    // The widget's events come from cross-origin iframes; keep them out of the zone for
    // the same reason the widget itself is loaded outside it.
    this.ngZone.runOutsideAngular(() => {
      // `ButtonClicked` only fires on the very first click, so every opening of the window
      // counts instead.
      const initiation$ = fromEvent<Event>(win, 'onEmbeddedMessagingWindowMaximized');

      // With verified users the header only offers minimize, which is the customer's
      // "close"; the Close (X) button is kept in case the deployment ever shows it.
      // `clearSession` also closes the window, so closes from our own session reset (on
      // page load and after idle timeout) are not the customer's.
      const chatClose$ = merge(
        fromEvent<Event>(win, 'onEmbeddedMessagingWindowMinimized'),
        fromEvent<Event>(win, 'onEmbeddedMessagingWindowClosed')
      ).pipe(filter(() => !this.chatSession.isResetting));

      // The widget gives no signal for focusing its text field, so the customer's first
      // message stands in for it. Re-armed once the window closes so each open is counted.
      const chatStart$ = fromEvent<Event>(win, 'onEmbeddedMessageSent').pipe(
        filter(isCustomerMessage),
        take(1),
        repeat({
          delay: () =>
            merge(
              fromEvent<Event>(win, 'onEmbeddedMessagingWindowMinimized'),
              fromEvent<Event>(win, 'onEmbeddedMessagingWindowClosed'),
              fromEvent<Event>(win, 'onEmbeddedMessagingConversationClosed')
            )
        })
      );

      this.subscription = merge(
        initiation$.pipe(map(() => CHAT_BOT_ANALYTICS_EVENTS.initiation)),
        chatStart$.pipe(map(() => CHAT_BOT_ANALYTICS_EVENTS.chatStart)),
        chatClose$.pipe(map(() => CHAT_BOT_ANALYTICS_EVENTS.chatClose))
      )
        .pipe(
          concatLatestFrom(() => [
            this.store.select(selectPolicyAnalytics),
            this.store.select(selectTrackId)
          ])
        )
        .subscribe(([event, policyAnalytics, trackId]) =>
          this.trackEvent(event, policyAnalytics?.partner_id_list, trackId)
        );
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }

  private trackEvent(
    event: ChatBotAnalyticsEvent,
    partnerIdList: string | undefined,
    trackId: string | undefined
  ): void {
    this.analyticsFacade.trackEvent({
      event,
      partner_id_list: partnerIdList,
      track_id: trackId
    });
  }
}
